/**
 * Textos POR DEFECTO de las secciones del builder, en inglés — SOLO EE.UU.
 *
 * Muchas secciones se guardan en la base solo con sus colores (FAQ, CTAModerno,
 * HeroBasic, VehicleCarousel, FloatingWhatsApp, WhatsAppButton…): sus textos no
 * están en el árbol y Craft los completa con `craft.props` de cada componente,
 * que están en español. Un sitio de EE.UU. mostraba así "Preguntas frecuentes",
 * "Ver detalles", "¿Tienes dudas? Escríbenos"…
 *
 * Acá, al renderizar y SOLO para tenants de EE.UU., se completan en inglés las
 * props que el nodo NO trae y que Craft llenaría con un texto de fábrica (mapa
 * `DEFAULTS_EN`). Diferencia con Brasil (`defaultsPt.ts`): NUNCA se toca una
 * prop que el árbol trae guardada —aunque coincida con un texto de fábrica—,
 * porque lo que está en el árbol lo escribió (o lo dejó) la automotora. Nada se
 * guarda: la base no cambia.
 *
 * Para el resto de los tenants no se llama nunca (el que llama gatea por país),
 * y si no hay nada que completar devuelve EXACTAMENTE lo que recibió.
 */
import { siteEs } from '@/i18n/locales/es/site';
import { siteEn } from '@/i18n/locales/en/site';
import { DEFAULTS_EN } from '../i18n/defaultsEn';

/**
 * Valores que el código del sitio COMPARA (no son solo texto a mostrar): nunca
 * se traducen. 'Nuevo' es el centinela de `newBadgeText` (VehicleVerticalCard:
 * `=== 'Nuevo'` rota las etiquetas automáticas, que ya salen en inglés por t()).
 */
const NO_TRADUCIR = new Set(['Nuevo', 'Todos', 'Todas', 'Publicado', 'Reservado', 'Vendido']);

let mapa: Map<string, string> | null = null;

/** es → en, armado una sola vez. */
const mapaEsEn = (): Map<string, string> => {
  if (mapa) return mapa;
  const m = new Map<string, string>();
  const es = siteEs.builderDefaults as Record<string, string>;
  const en = siteEn.builderDefaults as Record<string, string>;
  for (const clave of Object.keys(es)) {
    const destino = en[clave];
    if (typeof destino === 'string' && destino && destino !== es[clave]) m.set(es[clave], destino);
  }
  for (const [origen, destino] of Object.entries(DEFAULTS_EN)) {
    if (destino && destino !== origen) m.set(origen, destino);
  }
  for (const s of NO_TRADUCIR) m.delete(s);
  mapa = m;
  return m;
};

/** El texto de fábrica en inglés, o el mismo texto si no es uno de fábrica. */
export const textoDefaultEn = (texto: string): string => mapaEsEn().get(texto) ?? texto;

const esObjetoPlano = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype;

/**
 * Traduce en profundidad un DEFAULT de `craft.props` (strings sueltos, arrays y
 * objetos planos: las preguntas del FAQ, los links del navbar, `cardSettings`…).
 * Devuelve la MISMA referencia si no cambió nada.
 */
const traducir = (v: unknown): unknown => {
  if (typeof v === 'string') return textoDefaultEn(v);
  if (Array.isArray(v)) {
    let cambio = false;
    const out = v.map((x) => {
      const y = traducir(x);
      if (y !== x) cambio = true;
      return y;
    });
    return cambio ? out : v;
  }
  if (esObjetoPlano(v)) {
    let cambio = false;
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v)) {
      const y = traducir(x);
      if (y !== x) cambio = true;
      out[k] = y;
    }
    return cambio ? out : v;
  }
  return v;
};

type NodoSerializado = {
  type?: { resolvedName?: string } | string;
  props?: Record<string, unknown>;
  [k: string]: unknown;
};

/** Mapa de nodos serializados: { ROOT: {...}, id: {...} }. */
const completarNodos = (
  nodos: Record<string, unknown>,
  resolver: Record<string, unknown>
): Record<string, unknown> | null => {
  let cambio = false;
  const out: Record<string, unknown> = {};
  for (const [id, valor] of Object.entries(nodos)) {
    const nodo = valor as NodoSerializado;
    const nombre = esObjetoPlano(nodo) && typeof nodo.type === 'object' ? nodo.type?.resolvedName : undefined;
    const componente = nombre ? (resolver[nombre] as { craft?: { props?: Record<string, unknown> } } | undefined) : undefined;
    const porDefecto = componente?.craft?.props;
    if (!esObjetoPlano(nodo) || !esObjetoPlano(nodo.props) || !esObjetoPlano(porDefecto)) {
      out[id] = valor;
      continue;
    }
    // Solo lo que el árbol NO trae: lo guardado queda como está.
    let props: Record<string, unknown> | null = null;
    for (const k of Object.keys(porDefecto)) {
      if (Object.prototype.hasOwnProperty.call(nodo.props, k)) continue;
      const y = traducir(porDefecto[k]);
      if (y !== porDefecto[k]) {
        props = props ?? { ...nodo.props };
        props[k] = y;
      }
    }
    out[id] = props ? { ...nodo, props } : valor;
    if (props) cambio = true;
  }
  return cambio ? out : null;
};

/**
 * Árbol del builder con los textos de fábrica que el árbol no trae, en inglés.
 * Acepta el árbol como objeto o como JSON en texto, plano (`{ ROOT, … }`) o
 * envuelto (`{ nodes: { ROOT, … } }`). Si no hay nada que completar —o el árbol
 * no se entiende— devuelve exactamente lo que recibió.
 */
export function completarDefaultsDelArbolEn<T>(datos: T, resolver: Record<string, unknown>): T {
  let arbol: unknown = datos;
  if (typeof datos === 'string') {
    try {
      arbol = JSON.parse(datos);
    } catch {
      return datos;
    }
  }
  if (!esObjetoPlano(arbol)) return datos;

  if (esObjetoPlano(arbol.nodes)) {
    const nodos = completarNodos(arbol.nodes, resolver);
    return nodos ? ({ ...arbol, nodes: nodos } as unknown as T) : datos;
  }
  const nodos = completarNodos(arbol, resolver);
  return nodos ? (nodos as unknown as T) : datos;
}
